(() => {
  const SELECTOR = 'variant-selects';

  /**
   * Reads all real Shopify variants embedded in the variant picker.
   */
  function getVariants(picker) {
    const jsonElement = picker.querySelector('[data-linked-variants]');

    if (!jsonElement) return [];

    try {
      return JSON.parse(jsonElement.textContent);
    } catch (error) {
      console.error('Linked variants: unable to read variant data.', error);
      return [];
    }
  }

  /**
   * Returns each option group in the order Shopify displays it.
   *
   * Dawn uses:
   * - fieldsets for pill/button selectors
   * - .product-form__input--dropdown containers for dropdown selectors
   */
  function getOptionGroups(picker) {
    return Array.from(
      picker.querySelectorAll(
        ':scope > fieldset.product-form__input, :scope > .product-form__input--dropdown'
      )
    );
  }

  /**
   * Returns the selected value from a button or dropdown option group.
   */
  function getSelectedValue(group) {
    const select = group.querySelector('select');

    if (select) {
      return select.value;
    }

    const checkedInput = group.querySelector('input[type="radio"]:checked');

    return checkedInput ? checkedInput.value : null;
  }

  /**
   * Returns the option elements belonging to one option group.
   */
  function getChoices(group) {
    const select = group.querySelector('select');

    if (select) {
      return Array.from(select.options).map((option) => ({
        control: option,
        value: option.value,
        label: null,
        type: 'option',
      }));
    }

    return Array.from(group.querySelectorAll('input[type="radio"]')).map(
      (input) => ({
        control: input,
        value: input.value,
        label: group.querySelector(`label[for="${CSS.escape(input.id)}"]`),
        type: 'radio',
      })
    );
  }

  /**
   * Shows or hides a choice.
   *
   * We hide only combinations that do not exist. We do not use the
   * variant's "available" property here, because a real sold-out variant
   * should still remain visible.
   */
  function setChoiceVisibility(choice, visible) {
    if (choice.type === 'option') {
      choice.control.hidden = !visible;
      choice.control.disabled = !visible;
      return;
    }

    choice.control.hidden = !visible;
    choice.control.disabled = !visible;

    if (choice.label) {
      choice.label.hidden = !visible;
    }
  }

  /**
   * Finds which values are valid for the current option group, based on
   * all preceding selections.
   */
  function getValidValues(variants, selectedValues, optionIndex) {
    return new Set(
      variants
        .filter((variant) => {
          for (let index = 0; index < optionIndex; index += 1) {
            if (
              selectedValues[index] &&
              variant.options[index] !== selectedValues[index]
            ) {
              return false;
            }
          }

          return true;
        })
        .map((variant) => variant.options[optionIndex])
    );
  }

  /**
   * Selects the first valid value when the current selection has become
   * invalid, for example when changing from Pilules / 5g to Liquid.
   */
  function selectFirstValidChoice(group, validValues) {
    const choices = getChoices(group);

    const currentChoice = choices.find((choice) => {
      if (choice.type === 'option') {
        return choice.control.selected;
      }

      return choice.control.checked;
    });

    if (currentChoice && validValues.has(currentChoice.value)) {
      return false;
    }

    const firstValidChoice = choices.find((choice) =>
      validValues.has(choice.value)
    );

    if (!firstValidChoice) return false;

    if (firstValidChoice.type === 'option') {
      firstValidChoice.control.selected = true;

      firstValidChoice.control
        .closest('select')
        .dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      firstValidChoice.control.checked = true;
      firstValidChoice.control.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }

    return true;
  }

  /**
   * Links each option to the selections made before it.
   *
   * Option 1 remains fully visible.
   * Option 2 is filtered by Option 1.
   * Option 3, when present, is filtered by Options 1 and 2.
   */
  function updatePicker(picker) {
    if (!picker || picker.dataset.linkedVariantsUpdating === 'true') {
      return;
    }

    const variants = getVariants(picker);
    const groups = getOptionGroups(picker);

    if (!variants.length || groups.length < 2) return;

    picker.dataset.linkedVariantsUpdating = 'true';

    const selectedValues = groups.map(getSelectedValue);
    let selectionChanged = false;

    for (let optionIndex = 1; optionIndex < groups.length; optionIndex += 1) {
      const group = groups[optionIndex];
      const validValues = getValidValues(
        variants,
        selectedValues,
        optionIndex
      );

      getChoices(group).forEach((choice) => {
        setChoiceVisibility(choice, validValues.has(choice.value));
      });

      if (!selectionChanged) {
        selectionChanged = selectFirstValidChoice(group, validValues);
      }

      selectedValues[optionIndex] = getSelectedValue(group);
    }

    delete picker.dataset.linkedVariantsUpdating;
  }

  /**
   * Updates all visible product pickers, including featured products
   * and quick-add areas where applicable.
   */
  function updateAllPickers() {
    document.querySelectorAll(SELECTOR).forEach(updatePicker);
  }

  /**
   * Run after the initial page has loaded.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAllPickers);
  } else {
    updateAllPickers();
  }

  /**
   * Update immediately after the shopper changes an option.
   */
  document.addEventListener('change', (event) => {
    const picker = event.target.closest(SELECTOR);

    if (!picker) return;

    window.requestAnimationFrame(() => updatePicker(picker));
  });

  /**
   * Dawn refreshes the variant picker HTML after a variant selection.
   * This observer reapplies the linking when that replacement occurs.
   */
  const observer = new MutationObserver((mutations) => {
    const pickerWasAdded = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))
      )
    );

    if (pickerWasAdded) {
      window.requestAnimationFrame(updateAllPickers);
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  /**
   * Dawn also announces when the main product information has loaded.
   */
  document.addEventListener('product-info:loaded', updateAllPickers);
})();